import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MatDialog } from '@angular/material/dialog';
import { switchMap, takeUntil } from 'rxjs/operators';
import { SpinnerService } from 'src/app/services/spinner/spinner.service';
import { ConsentService } from 'src/app/services/consent/consent.service';
import { ComponentState } from '../../../base-view-edit-components/component-state.enum';
import { BaseEditComponent } from '../../../base-view-edit-components/base-edit/base-edit.component';
import { HipaaLoadErrorDialogComponent } from 'src/app/shared/components/hipaa-load-error-dialog/hipaa-load-error-dialog.component';
import DOMPurify from 'dompurify';

@Component({
  selector: 'mpp-hipaa-authorization-edit',
  templateUrl: './hipaa-authorization-edit.component.html',
  styleUrls: ['./hipaa-authorization-edit.component.scss'],
  standalone: false,
})
export class HipaaAuthorizationEditComponent extends BaseEditComponent implements OnInit {
  @Output() hipaaChanged = new EventEmitter<boolean>();
  @Input() hipaaConsented: boolean;
  @Input() consentUrl: string;
  @Input() preloadedHtml: string;

  public consentContent: SafeHtml;
  public isLoadingContent = true;
  public contentLoadError = false;

  form = this.fb.group({
    allowHipaa: [false],
  });

  constructor(
    private fb: UntypedFormBuilder,
    private spinner: SpinnerService,
    private consentService: ConsentService,
    private http: HttpClient,
    private sanitizer: DomSanitizer,
    private dialog: MatDialog,
  ) {
    super();
  }

  ngOnInit(): void {
    this.form.controls.allowHipaa.setValue(this.hipaaConsented);
    if (this.preloadedHtml) {
      const sanitizedHtml = DOMPurify.sanitize(this.preloadedHtml, {
        USE_PROFILES: { html: true },
      });
      this.consentContent = this.sanitizer.bypassSecurityTrustHtml(sanitizedHtml);
      this.isLoadingContent = false;
    } else if (this.consentUrl) {
      this.loadConsentContent(this.consentUrl);
    } else {
      this.handleContentLoadError(new Error('No HIPAA consent url'));
    }
  }

  private loadConsentContent(url: string): void {
    this.isLoadingContent = true;

    // Signal the web platform to the backend so it returns JSON { consentUrl } instead of a cross-origin 302 redirect (the mobile path), which the browser can't follow due to CORS.
    // Sent as a query param rather than an RMDHandsetPlatform header to avoid a CORS preflight.
    let backendUrl: URL;
    try {
      backendUrl = new URL(url);
      backendUrl.searchParams.set('platform', 'Web');
    } catch (error) {
      // new URL() throws synchronously on a malformed/relative url, outside the observable's error handler
      this.handleContentLoadError(error);
      return;
    }

    this.http
      .get<{ consentUrl: string }>(backendUrl.toString())
      .pipe(
        switchMap((response) => {
          if (!response.consentUrl) {
            throw new Error('No consentUrl in response');
          }
          return this.http.get(response.consentUrl, { responseType: 'text' });
        }),
        takeUntil(this.unsubscribe$),
      )
      .subscribe(
        (html) => {
          const sanitizedHtml = DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
          this.consentContent = this.sanitizer.bypassSecurityTrustHtml(sanitizedHtml);
          this.isLoadingContent = false;
        },
        (error) => {
          this.handleContentLoadError(error);
        },
      );
  }

  // AC MWEB-1787.7.2: show an error dialog and exit the consent-update flow without saving.
  private handleContentLoadError(error: unknown): void {
    console.error('[HipaaAuthorizationEdit] Failed to load consent content:', error);
    this.isLoadingContent = false;
    this.contentLoadError = true;

    this.dialog
      .open(HipaaLoadErrorDialogComponent, { disableClose: true })
      .afterClosed()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => this.cancel());
  }

  async save(): Promise<void> {
    if (this.isLoadingContent || this.contentLoadError) {
      return;
    }
    const spinner = this.spinner.show();
    const allowHipaa = this.form.get('allowHipaa').value;

    const consentCall = allowHipaa
      ? this.consentService.acceptHipaaConsent()
      : this.consentService.declineHipaaConsent();

    consentCall.pipe(takeUntil(this.unsubscribe$)).subscribe(
      (response) => {
        spinner.hide();
        if (response.result?.success) {
          this.stateChanged.next(ComponentState.View);
          this.hipaaChanged.next(allowHipaa);
        }
      },
      (error) => {
        console.error('[HipaaAuthorizationEdit] Save failed:', error);
        spinner.hide();
      },
    );
  }
}
